import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  CalendarSearch,
  CalendarCheck,
  Clock,
  Banknote,
  UserCircle,
  LogOut,
  Bell,
  ShieldCheck,
  Menu,
  X,
  MessageSquare,
  HelpCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '../ui/sheet';
import PWAInstallBanner from '../PWAInstallBanner';
import Walkthrough from '../Walkthrough/Walkthrough';
import { Logo } from '../Logo';
import { useIsLargeScreen } from '../../hooks/useIsLargeScreen';

export const WorkerLayout = () => {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Only one layout (desktop sidebar OR mobile bottom-nav) carries the
  // data-walkthrough attribute at a time — otherwise Joyride anchors on the
  // hidden one and renders nothing.
  const isLargeScreen = useIsLargeScreen();

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications/unread-count');
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Phase 1 UX simplification: 4 primary nav items. Active shift & till
  // stay accessible from the confirmed shift itself. Updates & Help are
  // reached via Profile — see WorkerProfile for the entry points.
  const navItems = [
    { to: '/worker', icon: CalendarSearch, label: 'Find Shifts', end: true, walkthroughId: 'wt-worker-available' },
    { to: '/worker/my-shifts', icon: CalendarCheck, label: 'My Shifts', walkthroughId: 'wt-worker-my-shifts' },
    { to: '/worker/chat', icon: MessageSquare, label: 'Messages', walkthroughId: 'wt-worker-messages' },
    { to: '/worker/profile', icon: UserCircle, label: 'Profile', walkthroughId: 'wt-worker-profile' },
  ];

  const NavItem = ({ item, mobile = false }) => (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={() => mobile && setMobileMenuOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
          mobile ? 'text-base' : 'text-sm'
        } ${
          isActive
            ? 'bg-white/10 text-white font-semibold'
            : 'text-white/70 hover:bg-white/5 hover:text-white'
        }`
      }
      data-testid={`nav-${item.label.toLowerCase()}`}
      data-walkthrough={!mobile && isLargeScreen ? item.walkthroughId : undefined}
    >
      <item.icon className="h-5 w-5" />
      <span>{item.label}</span>
      {item.badge > 0 && (
        <Badge className="ml-auto bg-[#DA291C] text-white text-xs px-2">
          {item.badge}
        </Badge>
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <Walkthrough />
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:flex lg:w-64 lg:flex-col bg-[#0f1b3d]">
        <div className="flex flex-col flex-1 px-4 py-6">
          {/* Logo */}
          <div className="flex items-center gap-3 px-4 mb-8" data-testid="worker-sidebar-logo">
            <Logo size="sm" variant="light" linkTo="/worker" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1">
            {navItems.map((item) => (
              <NavItem key={item.to} item={item} />
            ))}
          </nav>

          {/* User section */}
          <div className="border-t border-white/10 pt-4 mt-4">
            <div className="px-4 py-3">
              <p className="text-sm font-medium text-white">{user?.fullName}</p>
              <p className="text-xs text-white/60">
                {user?.smartIdVerified ? 'Verified Worker' : 'Worker'}
              </p>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-white/70 hover:text-white hover:bg-white/5 px-4"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-[#0f1b3d] text-white">
        <div className="flex items-center justify-between px-4 py-4">
          <Logo size="xs" variant="light" linkTo="/worker" />
          <div className="flex items-center gap-1">
            <NavLink
              to="/worker/notifications"
              aria-label="Notifications"
              className="relative inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg hover:bg-white/10 transition-colors"
              data-testid="mobile-header-bell-btn"
            >
              <Bell className="h-6 w-6" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 h-5 w-5 bg-[#DA291C] rounded-full flex items-center justify-center text-xs font-bold">
                  {unreadCount}
                </span>
              )}
            </NavLink>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-11 w-11" data-testid="mobile-menu-trigger">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-[#0f1b3d] border-0 p-0">
                <SheetTitle className="sr-only">Worker navigation menu</SheetTitle>
                <SheetDescription className="sr-only">Navigation menu with links to all worker pages</SheetDescription>
                <div className="flex flex-col h-full px-4 py-6">
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="h-7 w-7 text-white" />
                      <span className="text-lg font-bold text-white font-['Public_Sans']">Menu</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-white hover:bg-white/10 h-11 w-11"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-menu-close-btn"
                    >
                      <X className="h-6 w-6" />
                    </Button>
                  </div>
                  <nav className="flex-1 space-y-1">
                    {navItems.map((item) => (
                      <NavItem key={item.to} item={item} mobile />
                    ))}
                  </nav>
                  <div className="border-t border-white/10 pt-4 mt-4">
                    <div className="px-4 py-3">
                      <p className="text-sm font-medium text-white">{user?.fullName}</p>
                      <p className="text-xs text-white/60">
                        {user?.smartIdVerified ? 'Verified Worker' : 'Worker'}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-3 text-white/70 hover:text-white hover:bg-white/5 px-4 h-12"
                      onClick={handleLogout}
                      data-testid="mobile-menu-signout-btn"
                    >
                      <LogOut className="h-5 w-5" />
                      Sign Out
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64">
        <div className="min-h-screen pb-24 lg:pb-0">
          <PWAInstallBanner />
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation - key items only */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 mobile-nav">
        <div className="flex justify-around items-stretch">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2 px-2 relative min-h-[56px] flex-1 ${
                  isActive ? 'text-[#DA291C]' : 'text-slate-500'
                }`
              }
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              data-walkthrough={!isLargeScreen ? item.walkthroughId : undefined}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-[11px] font-medium leading-tight">{item.label}</span>
              {item.badge > 0 && (
                <span className="absolute top-1 right-2 h-4 w-4 bg-[#DA291C] rounded-full flex items-center justify-center text-[9px] font-bold text-white">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default WorkerLayout;
